import { all, takeEvery, put, call } from 'redux-saga/effects';
import actions from './actions';
import { sendRequest } from '../../../helpers/saga';
import { logger } from '../../../helpers/logger';
import notifications from '../../../redux/notifications/actions';
import applicantActions from '../applicant/actions';
import Api from '../../../helpers/api';
import { t } from '../../../i18n';

const approveApplicantApi = new Api('applicants/:id/approve');
const denyApplicantApi = new Api('applicants/:id/deny');
const interviewApi = new Api('interviews/:id');
const interviewFormDataApi = new Api('applicants/:id/interviews/form_data');
const interviewersApi = new Api('administrators/list?pageSize=9999');
const accreditationsApi = new Api('applicants/:applicant_id/accreditations/list/:skill_id');
const notScheduledInterviewStartedApi = new Api('applicants/:id/interviews/start_not_scheduled');

const defaultInterviewData = {
  positions: [],
  interviewer: {},
  status: { id: 0 },
  denied: false,
  engagement_source: '',
  id: '',
  identity_document_type_1st: null,
  identity_document_type_2nd: null,
  interview_note: '',
  interview_time: '',
};

function* startNotScheduledInterview({ payload: { id } }) {
  try {
    yield call(sendRequest, notScheduledInterviewStartedApi.updateItem, { id }, notifications.REQUEST_ERROR);
  } catch (error) {
    logger.error(error);
  }
}

function* loadFormData({ payload: { applicant, admin, interview_id } }) {
  try {
    const { id } = applicant;

    let interview = { ...defaultInterviewData };

    const { interviewers, formData, interviewData } = yield all({
      interviewers: call(sendRequest, interviewersApi.load, {}, notifications.REQUEST_ERROR),
      formData: call(sendRequest, interviewFormDataApi.load, { id }, notifications.REQUEST_ERROR),
      interviewData: interview_id
        ? call(sendRequest, interviewApi.load, { id: interview_id }, notifications.REQUEST_ERROR)
        : undefined,
    });

    if (interview_id && interviewData) {
      interview = interviewData.data.success.data;
    }

    if (interviewers && formData) {
      for (let i = 0; i < interview.positions.length; i += 1) {
        const tier = { tier_id: interview.positions[i].skill_id };
        yield put(actions.getAccreditations({ index: i, id, tier }));
      }

      if (!interview.id) {
        yield put(actions.startNotScheduledInterview({ id }));
      }

      yield put(
        actions.loadFormDataSuccess({
          ...formData.data.success.data,
          applicant: interview.applicant || applicant,
          interviewers: interviewers.data.data,
          interviewer_id: (interview.interviewer && interview.interviewer.id) || admin.id,
          interview,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* approveApplicant({ payload: { applicant, callback } }) {
  try {
    const result = yield call(
      sendRequest,
      approveApplicantApi.createItem,
      {
        data: { applicant: { ...applicant } },
        id: applicant.id,
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      yield put(actions.approveApplicantSuccess({}));
      callback();
      yield put(actions.clearStore());

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.applicants.talent_approved_successfully', { item: t('dash.applicants.applicant_title') }),
        }),
      );
    } else {
      yield put(
        actions.approveApplicantFail({
          message: t('dash.applicants.approve_applicant_error'),
          loading: false,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* denyApplicant({ payload: { applicant, callback } }) {
  try {
    const result = yield call(
      sendRequest,
      denyApplicantApi.createItem,
      {
        data: { applicant: { ...applicant } },
        id: applicant.id,
      },
      notifications.REQUEST_ERROR,
    );

    const message = (result && result.messages && result.messages.length && result.messages[0]) || null;

    if (result) {
      yield put(actions.denyApplicantSuccess({ message }));
      callback();

      if (!message) {
        yield put(actions.clearStore());
      }

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.applicants.talent_denied_successfully', { item: t('dash.applicants.applicant_title') }),
        }),
      );
    } else {
      yield put(
        actions.denyApplicantFail({
          message: message || t('dash.applicants.deny_applicant_error'),
          loading: false,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* getAccreditations({ payload: { id, index, tier } }) {
  try {
    const result = yield call(
      sendRequest,
      accreditationsApi.multipleParamsGetRequest,
      { applicant_id: id, skill_id: tier && tier.tier_id },
      notifications.REQUEST_ERROR,
    );

    if (result?.data?.success?.data) {
      yield put(actions.getAccredutationsSuccess({ index, data: result.data.success.data.records }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadApplicantData({ payload }) {
  try {
    yield put(applicantActions.loadData(payload));
  } catch (error) {
    logger.error(error);
  }
}

function* updateInterview({ payload: { interview, callback } }) {
  try {
    const result = yield call(
      sendRequest,
      interviewApi.updateItem,
      {
        data: { interview },
        id: interview.id,
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      yield put(actions.updateInterviewSuccess());
      callback();
      yield put(actions.clearStore());

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', { item: t('dash.applicants.interview_title') }),
        }),
      );
    } else {
      yield put(
        actions.updateInterviewFail({
          message: t('dash.common.notification.default_error'),
          loading: false,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.APPROVE_APPLICANT, approveApplicant),
    takeEvery(actions.DENY_APPLICANT, denyApplicant),
    takeEvery(actions.GET_ACCREDITATIONS, getAccreditations),
    takeEvery(actions.LOAD_APPLICANT_DATA, loadApplicantData),
    takeEvery(actions.LOAD_FORM_DATA, loadFormData),
    takeEvery(actions.OPEN_POPUP, loadFormData),
    takeEvery(actions.START_NOT_SCHEDULED_INTERVIEW, startNotScheduledInterview),
    takeEvery(actions.UPDATE_INTERVIEW, updateInterview),
  ]);
}
